import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, MoreHorizontal, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/components/ui/sonner";
import {
  displayTier,
  formatApiError,
  listMembers,
  matchMemberSupplier,
  roleLabel,
  setMemberRole,
  setMemberStatus,
  unlinkMemberSupplier,
} from "@/lib/api";
import { INDIAN_STATES } from "@/components/auth/AuthScreen";
import { SupplierLinkDialog } from "@/components/SupplierLinkDialog";
import { ADMIN_MEMBERS } from "@/constants/testIds";

const ANY = "__any__";
const STATUS_META = {
  active: ["Active", "bg-brand-teal-soft text-brand"],
  on_hold: ["On hold", "bg-[#fbf1dd] text-brand-gold"],
  suspended: ["Suspended", "bg-[#fdeee9] text-[#b45309]"],
  terminated: ["Terminated", "bg-[#fbe9e7] text-brand-danger"],
};
const STATUS_ACTIONS = [
  ["active", "Reinstate (active)"],
  ["on_hold", "Put on hold"],
  ["suspended", "Suspend"],
  ["terminated", "Terminate"],
];
const ROLES = ["customer", "ambassador", "trade_partner"];
const CUSTOMER_TIERS = ["silver", "gold", "platinum"];
const PARTNER_TIERS = ["authorised", "preferred", "premier", "elite"];
const STATUSES = ["active", "on_hold", "suspended", "terminated"];
const SUPPLIER_FILTERS = ["true", "false"];
const SUPPLIER_FILTER_LABELS = { true: "Linked", false: "Not linked" };
// Why the mobile match settled nothing, in the words an administrator can act on.
const MATCH_REASONS = {
  no_match: "No enabled ERP Supplier carries this mobile number",
  ambiguous: "More than one ERP Supplier carries this mobile number",
  erp_unavailable: "ERP could not be reached, please try again",
  supplier_already_linked: "That ERP Supplier already belongs to another member",
};
const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

const FilterSelect = ({ value, onChange, placeholder, options, render = cap }) => (
  <Select value={value || ANY} onValueChange={(v) => onChange(v === ANY ? "" : v)}>
    <SelectTrigger className="h-10 w-full rounded-[10px] border-black/10 bg-white text-[13px]">
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent className="max-h-[280px]">
      <SelectItem value={ANY}>{placeholder}</SelectItem>
      {options.map((o) => (
        <SelectItem key={o} value={o}>{render(o)}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const StatusBadge = ({ status }) => {
  const [label, cls] = STATUS_META[status] || [status, "bg-black/5 text-brand-muted"];
  return <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${cls}`}>{label}</span>;
};

export const AdminMembers = () => {
  const [filters, setFilters] = useState({
    q: "", role: "", customer_tier: "", partner_tier: "", status: "", state: "", city: "",
    supplier_linked: "",
  });
  const [qInput, setQInput] = useState("");
  const [busy, setBusy] = useState(null);
  const [linkTarget, setLinkTarget] = useState(null);

  const query = useQuery({
    queryKey: ["members", filters],
    queryFn: () => listMembers({ ...filters, page_size: 100 }),
    keepPreviousData: true,
  });

  const setF = (key) => (value) => setFilters((f) => ({ ...f, [key]: value }));
  const applySearch = (e) => {
    e.preventDefault();
    setFilters((f) => ({ ...f, q: qInput.trim() }));
  };

  const changeStatus = async (member, status) => {
    if (status === member.status) return;
    if (status === "terminated" &&
        !window.confirm(`Terminate ${member.name}'s account? This blocks all access.`)) return;
    setBusy(member.id);
    try {
      await setMemberStatus(member.id, status);
      toast.success(`${member.name} → ${STATUS_META[status][0]}`);
      query.refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update status");
    } finally {
      setBusy(null);
    }
  };

  const changeRole = async (member, role) => {
    if (role === member.role) return;
    if (!window.confirm(`Change ${member.name}'s role to ${roleLabel(role)}?`)) return;
    setBusy(member.id);
    try {
      await setMemberRole(member.id, role);
      toast.success(`${member.name} → ${roleLabel(role)}`);
      query.refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not change role");
    } finally {
      setBusy(null);
    }
  };

  const matchSupplier = async (member) => {
    setBusy(member.id);
    try {
      const result = await matchMemberSupplier(member.id);
      if (result.erp_supplier_id) {
        toast.success(`${member.name} is linked to ${result.erp_supplier_id}`);
      } else {
        toast.error(MATCH_REASONS[result.reason] || "No ERP Supplier matched");
      }
      query.refetch();
    } catch (error) {
      toast.error(formatApiError(error, "Could not match by mobile"));
    } finally {
      setBusy(null);
    }
  };

  const unlinkSupplier = async (member) => {
    if (!window.confirm(`Unlink ${member.name} from ${member.erp_supplier_id}?`)) return;
    setBusy(member.id);
    try {
      await unlinkMemberSupplier(member.id);
      toast.success(`${member.name} has no ERP Supplier now`);
      query.refetch();
    } catch (error) {
      toast.error(formatApiError(error, "Could not unlink the ERP Supplier"));
    } finally {
      setBusy(null);
    }
  };

  const items = query.data?.items || [];

  return (
    <section className="premium-card table-card" data-testid="admin-members">
      <div className="section-head">
        <div>
          <p className="eyebrow">DIRECTORY</p>
          <h2>Members</h2>
          <p>Search, filter, and manage member accounts.</p>
        </div>
        {!query.isLoading && (
          <span className="rounded-full bg-brand-gold-soft px-3 py-1 text-[13px] font-bold text-brand-gold-deep">
            {query.data?.total ?? 0} members
          </span>
        )}
      </div>

      <form onSubmit={applySearch} className="mb-3 flex gap-2">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-subtle" />
          <Input
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Search name, email, phone, or referral code"
            className="h-10 rounded-[10px] border-black/10 bg-white pl-9"
          />
        </div>
        <Button type="submit" className="h-10 rounded-[10px] bg-brand-forest text-white">Search</Button>
      </form>

      <div className="mb-5 grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-6">
        <FilterSelect value={filters.role} onChange={setF("role")} placeholder="All roles" options={ROLES} render={roleLabel} />
        <FilterSelect value={filters.customer_tier} onChange={setF("customer_tier")} placeholder="Any tier" options={CUSTOMER_TIERS} />
        <FilterSelect value={filters.partner_tier} onChange={setF("partner_tier")} placeholder="Any partner tier" options={PARTNER_TIERS} />
        <FilterSelect value={filters.status} onChange={setF("status")} placeholder="Any status" options={STATUSES} render={(s) => STATUS_META[s][0]} />
        <FilterSelect value={filters.state} onChange={setF("state")} placeholder="Any state" options={INDIAN_STATES} render={(s) => s} />
        <FilterSelect value={filters.supplier_linked} onChange={setF("supplier_linked")} placeholder="Any ERP Supplier" options={SUPPLIER_FILTERS} render={(value) => SUPPLIER_FILTER_LABELS[value]} />
        <Input
          value={filters.city}
          onChange={(e) => setF("city")(e.target.value)}
          placeholder="City"
          className="h-10 rounded-[10px] border-black/10 bg-white text-[13px]"
        />
      </div>

      {query.isLoading ? (
        <div className="grid place-items-center py-10"><Loader2 className="animate-spin text-brand-muted" size={22} /></div>
      ) : items.length ? (
        <div className="space-y-2">
          {items.map((m) => {
            const tier = displayTier(m);
            return (
              <div key={m.id} className="flex flex-wrap items-center justify-between gap-3 rounded-[12px] border border-black/5 bg-brand-surface p-3.5">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <b className="text-[14px]">{m.name}</b>
                    <span className="rounded-full bg-[#eaf2ef] px-2 py-0.5 text-[11px] font-bold text-brand">{roleLabel(m.role)}</span>
                    {tier && <span className="text-[11px] font-semibold text-brand-gold">{cap(tier)}</span>}
                    <StatusBadge status={m.status} />
                    <span
                      data-testid={ADMIN_MEMBERS.supplierBadge}
                      className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${m.erp_supplier_id ? "bg-brand-teal-soft text-brand" : "bg-black/5 text-brand-muted"}`}
                    >
                      {m.erp_supplier_id || "No ERP Supplier"}
                    </span>
                  </div>
                  <div className="mt-1 text-[12px] text-brand-muted">
                    {m.referral_code} · {m.email || "—"} · {m.phone || "—"} · {[m.city, m.state].filter(Boolean).join(", ") || "—"}
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline" disabled={busy === m.id} className="h-9 rounded-[10px]">
                      Actions <MoreHorizontal size={15} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52 rounded-[12px]">
                    <DropdownMenuLabel className="text-[11px] font-bold uppercase tracking-[.14em] text-brand-subtle">Account status</DropdownMenuLabel>
                    {STATUS_ACTIONS.map(([value, label]) => (
                      <DropdownMenuItem
                        key={value}
                        disabled={value === m.status}
                        onClick={() => changeStatus(m, value)}
                        className={value === "terminated" ? "text-brand-danger" : ""}
                      >
                        {label}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-[11px] font-bold uppercase tracking-[.14em] text-brand-subtle">Change role</DropdownMenuLabel>
                    {ROLES.map((r) => (
                      <DropdownMenuItem
                        key={r}
                        disabled={r === m.role}
                        onClick={() => changeRole(m, r)}
                      >
                        {roleLabel(r)}
                        {r === m.role && <span className="ml-auto text-[11px] text-brand-subtle">current</span>}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-[11px] font-bold uppercase tracking-[.14em] text-brand-subtle">ERP Supplier</DropdownMenuLabel>
                    {/* A relink needs an explicit unlink first, so both ways in are closed while a Supplier is held. */}
                    <DropdownMenuItem disabled={Boolean(m.erp_supplier_id)} onClick={() => setLinkTarget(m)}>Link ERP Supplier</DropdownMenuItem>
                    <DropdownMenuItem disabled={Boolean(m.erp_supplier_id)} onClick={() => matchSupplier(m)}>Match by mobile</DropdownMenuItem>
                    <DropdownMenuItem disabled={!m.erp_supplier_id} onClick={() => unlinkSupplier(m)}>Unlink</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="empty-inline"><span>No members match these filters.</span></div>
      )}

      {linkTarget && (
        <SupplierLinkDialog
          member={linkTarget}
          open={Boolean(linkTarget)}
          onOpenChange={(next) => !next && setLinkTarget(null)}
          onLinked={() => query.refetch()}
        />
      )}
    </section>
  );
};
